import cx from 'clsx';
import { useMemo } from 'react';
import { useMedia } from '#hooks/use-media';
import { CollapsableFooterMenu, FooterItem, MenuItem, MenuItemSpacer } from './components';
import { DualNavContext, type DualNavContextType } from './contexts/dual-nav-context';
import styles from './dual-nav.module.css';

type Slot = React.ReactNode | ((context: DualNavContextType) => React.ReactNode);

interface DualNavProps {
  switcherSlot?: Slot;
  mainMenuSlot?: Slot;
  footerMenuSlot?: Slot;
  logoSlot?: Slot;
  /**
   * The width state of the dual nav.
   * - collapsed: The dual nav is collapsed.
   * - expanded: The dual nav is expanded.
   * - responsive: The dual nav is responsive to the screen size and collapses when the screen is smaller than the small screen breakpoint.
   */
  widthState?: 'collapsed' | 'expanded' | 'responsive';
  className?: string;
}

function DualNav({
  switcherSlot,
  mainMenuSlot,
  footerMenuSlot,
  logoSlot,
  widthState = 'responsive',
  className,
}: DualNavProps) {
  const isSmallScreen = useMedia('sm');
  const smallScreenWidthState = isSmallScreen ? 'collapsed' : 'expanded';
  const widthStateValue = widthState === 'responsive' ? smallScreenWidthState : widthState;
  const contextValue = useMemo(() => ({ widthState: widthStateValue }), [widthStateValue]);

  const dualNavClassName = cx(
    styles.dualNav,
    widthStateValue === 'collapsed' ? styles.collapsed : styles.expanded,
    className
  );

  const switcherContent =
    typeof switcherSlot === 'function' ? switcherSlot(contextValue) : switcherSlot;
  const mainMenuEntries =
    typeof mainMenuSlot === 'function' ? mainMenuSlot(contextValue) : mainMenuSlot;
  const footerEntries =
    typeof footerMenuSlot === 'function' ? footerMenuSlot(contextValue) : footerMenuSlot;
  const logoContent = typeof logoSlot === 'function' ? logoSlot(contextValue) : logoSlot;

  return (
    <DualNavContext.Provider value={contextValue}>
      {}
      <nav className={dualNavClassName}>
        {switcherSlot ? <div className={styles.switcherSlot}>{switcherContent}</div> : null}
        <div className={styles.mainAndFooterWrapper}>
          <ul className={styles.mainMenuSlot} role={mainMenuSlot ? undefined : 'presentation'}>
            {mainMenuEntries}
          </ul>
          {footerMenuSlot ? (
            <CollapsableFooterMenu widthState={widthStateValue}>
              <ul className={styles.footerMenuSlot}>{footerEntries}</ul>
            </CollapsableFooterMenu>
          ) : null}
        </div>
        {logoSlot ? <div className={styles.logoSlot}>{logoContent}</div> : null}
        {/* TODO Submenu: <div className={styles.overlay} /> */}
      </nav>
    </DualNavContext.Provider>
  );
}

DualNav.MenuItem = MenuItem;
DualNav.MenuItemSpacer = MenuItemSpacer;
DualNav.FooterItem = FooterItem;

export { DualNav };
